/**
 * Result Scraper Service
 * Handles scraping UFC fight results from external sources
 * 
 * Note: Result scraping is a placeholder for future integration
 * This service provides a structure for future integration with other APIs
 * (e.g., ESPN API, UFC API, or web scraping)
 */

import type { Fight, FightResult } from '../types';

export interface ScrapedResult {
  fighter1Name: string;
  fighter2Name: string;
  winnerName: string;
  method: 'ko_tko' | 'submission' | 'decision_unanimous' | 'decision_majority' | 'decision_split' | 'dq' | 'draw' | 'no_contest';
  eventDate?: string;
  eventName?: string;
}

export interface ResultScraperResponse {
  success: boolean;
  results?: ScrapedResult[];
  error?: string;
}

/**
 * Result Scraper Service
 * Provides interface for scraping fight results
 */
export class ResultScraperService {
  private readonly apiKey?: string;

  constructor(apiKey?: string) {
    this.apiKey = apiKey;
  }

  /**
   * Check if API key is configured
   */
  hasApiKey(): boolean {
    return !!this.apiKey && this.apiKey.trim().length > 0;
  }

  /**
   * Scrape results for a specific fight
   * 
   * @param _fight - Fight to scrape results for (unused in placeholder implementation)
   * @returns Scraped result or null if not found
   */
  async scrapeFightResult(_fight: Fight): Promise<ScrapedResult | null> {
    // TODO: Implement actual API integration
    // For MVP, this is a placeholder that returns null
    // Future implementations could use:
    // - ESPN API
    // - UFC API
    // - Web scraping
    // - Custom backend service
    
    return null;
  }

  /**
   * Scrape results for multiple fights
   * 
   * @param fights - Array of fights to scrape results for
   * @returns Array of scraped results
   */
  async scrapeFightResults(fights: Fight[]): Promise<ScrapedResult[]> {
    const results: ScrapedResult[] = [];

    for (const fight of fights) {
      if (fight.status === 'completed') {
        continue; // Skip already completed fights
      }

      const result = await this.scrapeFightResult(fight);
      if (result) {
        results.push(result);
      }
    }

    return results;
  }

  /**
   * Match scraped result to fight by fighter names
   * 
   * @param scrapedResult - Scraped result from API
   * @param fight - Fight to match against
   * @returns True if fighters match
   */
  matchResultToFight(scrapedResult: ScrapedResult, fight: Fight): boolean {
    const fightNames = fight.fighters.map((f) => f.name.toLowerCase());
    const scrapedNames = [
      scrapedResult.fighter1Name.toLowerCase(),
      scrapedResult.fighter2Name.toLowerCase(),
    ];

    // Check if fighter names match (case-insensitive, partial match)
    return fightNames.every((name) =>
      scrapedNames.some((scrapedName) =>
        name.includes(scrapedName) || scrapedName.includes(name)
      )
    );
  }

  /**
   * Convert scraped result to FightResult format
   * 
   * @param scrapedResult - Scraped result from API
   * @param fight - Fight to convert result for
   * @returns FightResult or null if winner doesn't match
   */
  convertToFightResult(
    scrapedResult: ScrapedResult,
    fight: Fight
  ): FightResult | null {
    // Find winner by name match
    const winner = fight.fighters.find(
      (f) =>
        f.name.toLowerCase().includes(scrapedResult.winnerName.toLowerCase()) ||
        scrapedResult.winnerName.toLowerCase().includes(f.name.toLowerCase())
    );

    if (!winner) {
      return null;
    }

    // Map scraped result method to our method type
    // Use the method directly (it's already the correct type)
    return {
      winnerId: winner.id,
      method: scrapedResult.method,
    };
  }

  /**
   * Validate scraped result before processing
   * 
   * @param scrapedResult - Scraped result to validate
   * @param fight - Fight to validate against
   * @returns True if result is valid
   */
  validateResult(scrapedResult: ScrapedResult, fight: Fight): boolean {
    // Check if fighters match
    if (!this.matchResultToFight(scrapedResult, fight)) {
      return false;
    }

    // Check if winner is one of the fighters
    const winnerMatches = fight.fighters.some(
      (f) =>
        f.name.toLowerCase().includes(scrapedResult.winnerName.toLowerCase()) ||
        scrapedResult.winnerName.toLowerCase().includes(f.name.toLowerCase())
    );

    return winnerMatches;
  }
}

/**
 * Create Result Scraper service instance
 */
export function createResultScraperService(
  apiKey?: string
): ResultScraperService {
  return new ResultScraperService(apiKey);
}

